import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Heart, Loader2, Check, Sparkles, AlertTriangle, Globe } from 'lucide-react';
import api from '../utils/api';
import { isAuthenticated } from '../utils/auth';

export default function Paywall() {
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState('3_matches');
  const [pricing, setPricing] = useState({
    '3_matches': { amount: 249, symbol: '₹' },
    '10_matches': { amount: 499, symbol: '₹' }
  });
  const [currency, setCurrency] = useState('INR');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState('IN');
  const [detectedCountry, setDetectedCountry] = useState('');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    const loadData = async () => {
      try {
        // Get profile
        const profileRes = await api.get('/user/profile');
        setProfile(profileRes.data);

        // Check if profile is complete, if not redirect to profile setup
        if (!profileRes.data.is_profile_complete) {
          navigate('/profile-setup');
          return;
        }

        // Check if user gets free matches (women looking for men)
        if (profileRes.data.gets_free_matches) {
          if (profileRes.data.is_photo_verified) {
            // Verified women go directly to dashboard
            navigate('/dashboard');
            return;
          } else {
            // Unverified women need to verify first
            navigate('/verify-photo');
            return;
          }
        }

        // Detect country
        const geoRes = await api.get('/geo/detect');
        const country = geoRes.data.country_code || 'IN';
        setCountryCode(country);
        setDetectedCountry(geoRes.data.country_name || 'your region');

        // All regions currently check out via Razorpay (Stripe hidden from UI)

        // Get pricing for both packages
        const pricing3 = await api.get(`/geo/pricing?country_code=${country}&package_type=3_matches`);
        const pricing10 = await api.get(`/geo/pricing?country_code=${country}&package_type=10_matches`);
        
        const currencyMap = {
          'INR': '₹',
          'USD': '$',
          'GBP': '£',
          'EUR': '€'
        };
        
        const curr = pricing3.data.currency;
        setCurrency(curr);
        
        setPricing({
          '3_matches': {
            amount: pricing3.data.amount,
            symbol: currencyMap[curr] || '$'
          },
          '10_matches': {
            amount: pricing10.data.amount,
            symbol: currencyMap[curr] || '$'
          }
        });
      } catch (error) {
        console.error('Load error:', error);
      }
    };

    loadData();
  }, [navigate]);

  // The checkout script is in index.html, but ad-blockers/slow networks can drop it.
  // Load it on demand so the pay button never dead-ends.
  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const existing = document.getElementById('razorpay-checkout-js');
    if (existing) {
      existing.addEventListener('load', () => resolve(!!window.Razorpay));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(!!window.Razorpay);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const handlePayment = async () => {
    setLoading(true);
    let orderRes;
    try {
      orderRes = await api.post('/payment/razorpay/create-order', {
        package_type: selectedPackage
      }, {
        params: { country_code: countryCode }
      });
    } catch (error) {
      console.error('Create order error:', error);
      // Always show the real reason (e.g. "You have 1 active match. Unmatch first.")
      const detail = error.response?.data?.detail;
      toast.error(detail || 'We could not start the payment. Please check your connection and try again.');
      if (typeof detail === 'string' && detail.toLowerCase().includes('active match')) {
        setTimeout(() => navigate('/dashboard'), 2500);
      }
      setLoading(false);
      return;
    }

    const { order_id, amount, currency, key_id, matches } = orderRes.data;

    const scriptReady = await loadRazorpayScript();
    if (!scriptReady || !window.Razorpay) {
      toast.error('Payment window could not load. Please disable any ad-blocker or try another browser.');
      setLoading(false);
      return;
    }

    try {
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        order_id: order_id,
        name: 'BlindMatch',
        description: `${matches} Anonymous Matches`,
        prefill: {
          email: profile?.email || ''
        },
        notes: {
          package_type: selectedPackage,
          matches: matches
        },
        handler: async (response) => {
          try {
            const verifyRes = await api.post('/payment/razorpay/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              package_type: selectedPackage
            });
            
            const matchesGenerated = verifyRes.data.matches_generated || matches;
            if (matchesGenerated > 0) {
              toast.success(`Payment successful! Found ${matchesGenerated} matches for you!`);
            } else {
              toast.success(`Payment successful! We'll notify you when matches are available.`);
            }
            setTimeout(() => navigate('/dashboard'), 2000);
          } catch (error) {
            toast.error(
              error.response?.data?.detail ||
              'Payment received but confirmation failed. Contact payments@blindmatch.in with your payment id.'
            );
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          }
        },
        theme: {
          color: '#6B46C1'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        toast.error(response?.error?.description || 'Payment failed. No money was deducted.');
        setLoading(false);
      });
      razorpay.open();
    } catch (error) {
      console.error('Checkout open error:', error);
      toast.error('Could not open the payment window. Please try again.');
      setLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="app-container gradient-bg min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={40} />
      </div>
    );
  }

  const isRepeat = profile.total_matches_used > 0;
  const currentPricing = pricing[selectedPackage];

  return (
    <div className="app-container gradient-bg">
      <div className="px-4 py-8 min-h-screen flex items-center justify-center">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-6">
            <Heart className="text-primary fill-primary mx-auto mb-3 pulse-animation" size={48} />
            <h1 className="text-3xl font-bold text-white mb-2">
              {isRepeat ? 'Get More Matches' : 'Your Blind Matches'}
            </h1>
            <p className="text-gray-400">Choose your package</p>
          </div>

          {/* Package Selection */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* 3 Matches Package */}
            <button
              onClick={() => setSelectedPackage('3_matches')}
              className={`card-enhanced p-6 rounded-3xl transition-all duration-300 relative ${
                selectedPackage === '3_matches' 
                  ? 'border-2 border-primary shadow-lg shadow-primary/30' 
                  : 'border border-white/10'
              }`}
              data-testid="package-3-matches"
            >
              {selectedPackage === '3_matches' && (
                <div className="absolute top-3 right-3">
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <Check size={14} className="text-white" />
                  </div>
                </div>
              )}
              <div className="text-4xl mb-2">💜</div>
              <h3 className="text-white font-bold text-xl mb-1">3 Matches</h3>
              <p className="text-amber-400 font-bold text-2xl mb-1" data-testid="price-3-matches">
                {pricing['3_matches'].symbol}{pricing['3_matches'].amount}
              </p>
              <p className="text-gray-400 text-xs">Perfect to start</p>
            </button>

            {/* 10 Matches Package */}
            <button
              onClick={() => setSelectedPackage('10_matches')}
              className={`card-enhanced p-6 rounded-3xl transition-all duration-300 relative ${
                selectedPackage === '10_matches' 
                  ? 'border-2 border-primary shadow-lg shadow-primary/30' 
                  : 'border border-white/10'
              }`}
              data-testid="package-10-matches"
            >
              {selectedPackage === '10_matches' && (
                <div className="absolute top-3 right-3">
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                    <Check size={14} className="text-white" />
                  </div>
                </div>
              )}
              <div className="absolute top-2 left-2 bg-accent text-background text-xs font-bold px-2 py-1 rounded-full">
                BEST VALUE
              </div>
              <div className="text-4xl mb-2">🔥</div>
              <h3 className="text-white font-bold text-xl mb-1">10 Matches</h3>
              <p className="text-amber-400 font-bold text-2xl mb-1" data-testid="price-10-matches">
                {pricing['10_matches'].symbol}{pricing['10_matches'].amount}
              </p>
              <p className="text-gray-400 text-xs">More connections</p>
            </button>
          </div>

          {/* Selected Package Info Card */}
          <div className="glass rounded-3xl p-6 mb-6 border border-primary/30">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-400">You selected:</span>
              <span className="text-white font-bold">
                {selectedPackage === '3_matches' ? '3 Matches' : '10 Matches'}
              </span>
            </div>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 text-white">
                <span className="text-green-400 flex-shrink-0">✓</span>
                <span>{selectedPackage === '3_matches' ? '3' : '10'} quality anonymous matches</span>
              </div>
              <div className="flex items-start gap-3 text-white">
                <span className="text-green-400 flex-shrink-0">✓</span>
                <span>End-to-end encrypted chats</span>
              </div>
              <div className="flex items-start gap-3 text-white">
                <span className="text-green-400 flex-shrink-0">✓</span>
                <span>Share details only when YOU want</span>
              </div>
              <div className="flex items-start gap-3 text-white">
                <span className="text-green-400 flex-shrink-0">✓</span>
                <span>Get notified when matches are ready</span>
              </div>
            </div>

            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-lg">Total:</span>
                <span className="text-amber-400 font-bold text-3xl" data-testid="paywall-price">
                  {currentPricing.symbol}{currentPricing.amount}
                </span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={handlePayment}
            disabled={loading}
            className="w-full h-14 bg-accent hover:bg-accent/90 text-background font-bold text-lg rounded-full btn-glow transition-all duration-300 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="paywall-payment-button"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Processing...
              </>
            ) : (
              <>
                <Sparkles size={20} />
                Pay {currentPricing.symbol}{currentPricing.amount} & Get Matches
              </>
            )}
          </button>

          {/* Legal */}
          <p className="text-gray-500 text-xs text-center mt-4">
            By paying you agree to Terms. 18+ only.
          </p>
          {countryCode !== 'IN' && detectedCountry && (
            <p className="text-gray-500 text-xs text-center mt-2 flex items-center justify-center gap-1" data-testid="gateway-note">
              <Globe size={12} />
              Secure checkout in {currency} ({detectedCountry})
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
